import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { SeasonLink as Link } from "../../components/Seasons/SeasonLink";
import styles from './resultPage.module.css';
import avatarPlaceholder from '../../assets/icons/avatar.svg';
import { getSearchTeamProfile } from '../../api/search';
import { buildDisplayName } from '../../utils/userDisplay';

export function TeamResultPage() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError('');
      try {
        const response = await getSearchTeamProfile(Number(teamId));
        setTeam(response);
      } catch (err) {
        setError(err.message || 'Не удалось загрузить страницу команды.');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [teamId]);

  const captain = useMemo(
    () => team?.members?.find((item) => item.system_role === 'CAPTAIN') || null,
    [team]
  );

  if (loading) return <p className={styles.note}>Загружаем команду...</p>;
  if (error) return <p className={styles.error}>{error}</p>;
  if (!team) return <p className={styles.note}>Команда не найдена.</p>;
  const returnQuery = (searchParams.get('q') || team.name || '').trim();
  const returnSkill = (searchParams.get('skill') || '').trim();
  const backHref = `/search?q=${encodeURIComponent(returnQuery)}${returnSkill ? `&skill=${encodeURIComponent(returnSkill)}` : ''}`;
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(backHref);
  };

  return (
    <section className={styles.page}>
      <header className={styles.head}>
        <button type="button" className={styles.backLink} onClick={handleBack}>
          Вернуться назад
        </button>
        <h1 className={styles.title}>{team.name}</h1>
        <p className={styles.subtitle}>{team.captain_login ? `Капитан: @${team.captain_login}` : 'Капитан не назначен'}</p>
      </header>

      <article className={styles.heroCard}>
        <img
          className={styles.avatar}
          src={team.avatar_url || avatarPlaceholder}
          alt=""
          onError={(event) => {
            event.currentTarget.src = avatarPlaceholder;
          }}
        />
        <div className={styles.heroBody}>
          <div className={styles.chips}>
            <span className={styles.chip}>Баланс: {team.balance}</span>
            <span className={styles.chip}>Место #{team.rank_position}</span>
            <span className={styles.chip}>Участников: {team.member_count}</span>
          </div>
          {captain ? (
            <div className={styles.field}>
              <span>Капитан</span>
              <strong>{buildDisplayName(captain)} (@{captain.login})</strong>
            </div>
          ) : null}
        </div>
      </article>

      <article className={styles.block}>
        <h2 className={styles.blockTitle}>Актуальные вакансии</h2>
        {!team.vacancies?.length ? (
          <p className={styles.note}>Команда пока не публиковала вакансии.</p>
        ) : (
          <div className={styles.vacanciesList}>
            {team.vacancies.map((vacancy) => (
              <article key={vacancy.vacancy_id} className={styles.vacancyCard}>
                <div className={styles.vacancyHead}>
                  <strong>{vacancy.title}</strong>
                  <span className={styles.chip}>Открыта</span>
                </div>
                <p className={styles.vacancyDescription}>{vacancy.description}</p>
              </article>
            ))}
          </div>
        )}
      </article>

      <article className={styles.block}>
        <h2 className={styles.blockTitle}>Состав команды</h2>
        {!team.members?.length ? (
          <p className={styles.note}>В команде пока нет участников.</p>
        ) : (
          <div className={styles.cardsGrid}>
            {team.members.map((member) => (
              <Link key={member.user_id} to={`/search/users/${member.user_id}`} className={styles.miniCard}>
                <img
                  className={styles.miniAvatar}
                  src={member.avatar_url || avatarPlaceholder}
                  alt=""
                  onError={(event) => {
                    event.currentTarget.src = avatarPlaceholder;
                  }}
                />
                <div>
                  <p className={styles.miniTitle}>
                    {buildDisplayName(member)}
                  </p>
                  <p className={styles.miniMeta}>
                    @{member.login}
                  </p>
                  <p className={styles.miniMeta}>
                    {member.system_role}{member.custom_role ? ` · ${member.custom_role}` : ''}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </article>
    </section>
  );
}
